import { PAYMENT_DETAILS_FORMAT } from 'constant/paymentConstants';
import { creditCardTypeMap } from 'maps/creditCardMaps/creditCardMaps';

/**
 * Parses a credit card payment method into its internal structure.
 *
 * If the payment method does not have last four digits, returns null.
 * Otherwise, returns an object with card type, expiration date, and last four digits.
 *
 * @param paymentMethod The payment method object to be parsed.
 * @returns The internal credit card structure or null if not applicable.
 */
export const parseCreditCardToInternalStructure = paymentMethod => {
  if (!paymentMethod?.expirationDate) {
    return null;
  }
  return {
    cardType: paymentMethod.cardType,
    expirationDate: paymentMethod.expirationDate,
    lastFourDigits: paymentMethod.lastFourDigits,
  };
};

/**
 * Parses credit card details into a formatted string.
 *
 * Extracts the card type and expiration date, formats them, and returns a string
 * using the PAYMENT_DETAILS_FORMAT. If expiration date is missing, returns an empty string.
 *
 * @param {string} cardType - CardType of the credit card (e.g., 'VI', 'AMERICAN_EXPRESS').
 * @param {string} expirationDate - Expiration date of the credit card in 'MMYY' format.
 * @returns The formatted credit card details string.
 */
export const parseCreditCardDetails = ({ cardType, expirationDate }) => {
  if (!expirationDate) {
    return '';
  }
  const [month, year] = [
    expirationDate.substring(0, 2),
    expirationDate.substring(2, 4),
  ];

  const cardTypeLabel = creditCardTypeMap[cardType] ?? creditCardTypeMap['VI'];
  return PAYMENT_DETAILS_FORMAT.replace('%s', cardTypeLabel)
    .replace('%s', month)
    .replace('%s', year);
};
